/**
 * Which record a conversation warms, and the exact value it writes.
 *
 * Three paths logged a conversation and each decided this for itself:
 *   - Quick Log and the communication form stamped every type, notes
 *     included, and the form swallowed a failed stamp into console.warn;
 *   - the MCP's log_interaction stamped, but swallowed a prospect's failure;
 *   - logCall / logEmail / logMeeting — the functions anything new would
 *     reach for — stamped nothing at all.
 * None of them checked whether the record already held a later touch.
 *
 * PocketBase stores a date in one shape ("2026-09-11 14:03:00.000Z")
 * whichever shape it is sent in — checked on production. The shape matters
 * only when a date is written into a filter string, where it is compared as
 * text; pbTimestamp gives that shape so the two never differ.
 *
 * This decides it once. Pure: no network, no PocketBase client — the browser
 * and the MCP hold different clients, so each does its own write with what
 * this returns.
 */

/** The records a conversation can be with, and the field that remembers it. */
const TARGETS = {
  client: { collection: 'clients', field: 'last_contact_at' },
  prospect: { collection: 'prospects', field: 'last_contact_at' },
  contact: { collection: 'contacts', field: 'last_contact_date' },
} as const

export type TouchEntity = keyof typeof TARGETS

/**
 * The kinds of activity that are a conversation. A note is something written
 * down; a status change is something that happened to a record. Neither is
 * contact, and counting them would make a client nobody has spoken to in
 * months look warm because someone fixed a typo. A text is contact.
 */
export const CONVERSATION_TYPES = ['call', 'email', 'meeting', 'text'] as const

export function isConversation(activityType: string | null | undefined): boolean {
  return (CONVERSATION_TYPES as readonly string[]).includes(String(activityType ?? ''))
}

/** A moment in the shape PocketBase stores and compares: a space, not the ISO "T". */
export function pbTimestamp(at: Date = new Date()): string {
  if (Number.isNaN(at.getTime())) throw new Error('pbTimestamp: not a date')
  return at.toISOString().replace('T', ' ')
}

export interface TouchTarget {
  collection: (typeof TARGETS)[TouchEntity]['collection']
  field: (typeof TARGETS)[TouchEntity]['field']
  id: string
  value: string
}

/**
 * The stamp for one record, or null when the record type has no last-contact
 * field. Null is the answer for a deal or a contract — not an error, and not
 * a guess at which person on the deal was spoken to.
 */
export function touchTargetFor(entityType: string | null | undefined, entityId: string | null | undefined, at: Date = new Date()): TouchTarget | null {
  const kind = String(entityType ?? '') as TouchEntity
  const target = TARGETS[kind]
  if (!target || !entityId) return null
  return { collection: target.collection, field: target.field, id: String(entityId), value: pbTimestamp(at) }
}

/**
 * Which person an activity was with, from the fields it carries. The explicit
 * id columns win over the generic entity pair; a prospect has no column of
 * its own on activities and rides on entity_type/entity_id alone.
 */
export function touchedBy(activity: {
  client_id?: string | null
  contact_id?: string | null
  entity_type?: string | null
  entity_id?: string | null
}): { entityType: TouchEntity; entityId: string } | null {
  if (activity.client_id) return { entityType: 'client', entityId: activity.client_id }
  if (activity.contact_id) return { entityType: 'contact', entityId: activity.contact_id }
  const kind = String(activity.entity_type ?? '')
  if (kind in TARGETS && activity.entity_id) return { entityType: kind as TouchEntity, entityId: activity.entity_id }
  return null
}

/**
 * Whether a touch at `at` should replace what is on record. A conversation
 * logged late — a recording filed the next morning — must not make someone
 * look less recently contacted than they were. Unknown on record always
 * yields to a real touch.
 */
export function isNewerTouch(onRecord: string | null | undefined, at: Date): boolean {
  if (!onRecord) return true
  const recorded = new Date(String(onRecord).replace(' ', 'T'))
  if (Number.isNaN(recorded.getTime())) return true
  return at.getTime() > recorded.getTime()
}
